import { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigationType, NavigationType } from 'react-router';
import { VirtuosoHandle, StateSnapshot } from 'react-virtuoso';

const STORAGE_PREFIX = 'virtuoso_scroll_';
const RESTORE_COOLDOWN = 300;
const SAVE_DEBOUNCE = 200;

const useVirtuosoScrollRestoration = (storageKey: string) => {
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const currentStorageKey = `${STORAGE_PREFIX}${storageKey}`;
  const [restoredState, setRestoredState] = useState<StateSnapshot | undefined>(undefined);

  const [isRestoring, setIsRestoring] = useState(true);

  const restorationRef = useRef({
    restored: false,
    savingBlocked: false,
  });

  const saveDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const navigationType = useNavigationType();
  const shouldAttemptRestore = navigationType === NavigationType.Pop;

  useEffect(() => {
    return () => {
      if (saveDebounceTimerRef.current) {
        clearTimeout(saveDebounceTimerRef.current);
      }

      if (virtuosoRef.current) {
        virtuosoRef.current.getState((state) => {
          sessionStorage.setItem(currentStorageKey, JSON.stringify(state));
        });
      }
    };
  }, [currentStorageKey]);

  useEffect(() => {
    restorationRef.current = {
      restored: false,
      savingBlocked: false,
    };
    setRestoredState(undefined);
    setIsRestoring(true);
  }, [currentStorageKey]);

  useEffect(() => {
    if (!shouldAttemptRestore) {
      setIsRestoring(false);
      return;
    }

    if (restorationRef.current.restored) {
      setIsRestoring(false);
      return;
    }

    const savedStateJson = sessionStorage.getItem(currentStorageKey);

    if (!savedStateJson) {
      restorationRef.current.restored = true;
      setIsRestoring(false);
      return;
    }

    try {
      const savedState = JSON.parse(savedStateJson) as StateSnapshot;
      setRestoredState(savedState);
      restorationRef.current.restored = true;
      restorationRef.current.savingBlocked = true;

      setTimeout(() => {
        restorationRef.current.savingBlocked = false;
      }, RESTORE_COOLDOWN);
    } catch (error) {
      sessionStorage.removeItem(currentStorageKey);
      restorationRef.current.restored = true;
    } finally {
      setIsRestoring(false);
    }
  }, [shouldAttemptRestore, currentStorageKey, navigationType]);

  const onRangeChanged = useCallback(() => {
    if (restorationRef.current.savingBlocked) {
      return;
    }

    if (saveDebounceTimerRef.current) {
      clearTimeout(saveDebounceTimerRef.current);
    }

    saveDebounceTimerRef.current = setTimeout(() => {
      if (virtuosoRef.current) {
        virtuosoRef.current.getState((currentState) => {
          sessionStorage.setItem(currentStorageKey, JSON.stringify(currentState));
        });
      }
      saveDebounceTimerRef.current = null;
    }, SAVE_DEBOUNCE);
  }, [currentStorageKey]);

  const setVirtuosoRef = useCallback((instance: VirtuosoHandle | null) => {
    virtuosoRef.current = instance;
  }, []);

  return {
    virtuosoRef: setVirtuosoRef,
    onRangeChanged,
    restoreStateFrom: restoredState,
    isRestoring,
  };
};

export default useVirtuosoScrollRestoration;
